import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Edit, Trash2, MoreHorizontal, Filter, FileText, Calendar, CheckCircle, XCircle, AlertTriangle, Clock, Eye, Upload } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { getMsqCaseStudies, createMsqCaseStudy, updateMsqCaseStudy, deleteMsqCaseStudy, updateMsqCaseStudyStatus } from '../utils/msqCaseStudiesApi';
import { mockMsqCaseStudies, msqCaseStudyStatuses } from '../utils/mockMsqCaseStudyData';
import { uploadPdfFile } from '../utils/fileUpload';
import LoadingSpinner from '../components/LoadingSpinner';

const MSQCaseStudies = () => {
  const navigate = useNavigate();
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCaseStudy, setEditingCaseStudy] = useState(null);
  const [apiResponse, setApiResponse] = useState(null);

  // Add custom styles for Quill editor
  const quillStyles = `
    .ql-editor {
      min-height: 80px !important;
      max-height: 200px !important;
      overflow-y: auto !important;
    }
    .ql-toolbar {
      border-top: 1px solid #d1d5db !important;
      border-left: 1px solid #d1d5db !important;
      border-right: 1px solid #d1d5db !important;
      border-bottom: none !important;
    }
    .ql-container {
      border-bottom: 1px solid #d1d5db !important;
      border-left: 1px solid #d1d5db !important;
      border-right: 1px solid #d1d5db !important;
      border-top: none !important;
    }
  `;

  // Quill editor configuration
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ],
  };

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent', 'color', 'background',
    'align', 'link', 'image'
  ];
  
  const [formData, setFormData] = useState({
    title: '',
    status: 'active',
    description: '',
    module_number: '',
    video_url: '',
    meta: []
  });
  const [uploadingMetaImages, setUploadingMetaImages] = useState({});

  // Memoized onChange handlers for ReactQuill to prevent infinite loops
  const handleTitleChange = useCallback((value) => {
    setFormData(prev => ({ ...prev, title: value }));
  }, []);

  const handleDescriptionChange = useCallback((value) => {
    setFormData(prev => ({ ...prev, description: value }));
  }, []);

  // Attachment management
  const addMetaItem = () => {
    setFormData(prev => ({
      ...prev,
      meta: [...(prev.meta || []), {
        pdf_name: '',
        pdf_description: '',
        pdf_attach: ''
      }]
    }));
  };

  const removeMetaItem = (index) => {
    setFormData(prev => ({
      ...prev,
      meta: prev.meta.filter((_, i) => i !== index)
    }));
  };

  const updateMetaItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      meta: prev.meta.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const handleMetaPdfUpload = async (index, file) => {
    if (!file) return;

    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      alert('Please select a PDF file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    const pdfKey = `meta_${index}`;
    setUploadingMetaImages(prev => ({ ...prev, [pdfKey]: true }));

    try {
      const uploadedUrl = await uploadPdfFile(file, 'pdfs');
      updateMetaItem(index, 'pdf_attach', uploadedUrl);
    } catch (error) {
      console.error('Error uploading PDF:', error);
      alert('Failed to upload PDF. Please try again.');
    } finally {
      setUploadingMetaImages(prev => ({ ...prev, [pdfKey]: false }));
    }
  };

  // Ensure caseStudies is always an array
  const safeCaseStudies = Array.isArray(caseStudies) ? caseStudies : [];

  // Dynamic column configuration based on API response
  const getTableColumns = () => {
    if (safeCaseStudies.length === 0) return [];
    
    const sampleCaseStudy = safeCaseStudies[0];
    const columns = [];
    
    const getFieldValue = (caseStudy, possibleNames, defaultValue = null) => {
      for (const name of possibleNames) {
        if (caseStudy[name] !== undefined && caseStudy[name] !== null && caseStudy[name] !== '') {
          return caseStudy[name];
        }
      }
      return defaultValue;
    };
    
    
    // Always include case study title
    columns.push({
      key: 'title',
      label: 'Case Study Title',
      type: 'text',
      width: 'w-64'
    });
    
        // Always include status
    columns.push({
      key: 'status',
      label: 'Status',
      type: 'badge',
      width: 'w-32'
    });
    
    // Always include video URL
  {/** columns.push({
      key: 'video_url',
      label: 'Video URL',
      type: 'text',
      width: 'w-48'
    });*/}
    
    // Always include actions
    columns.push({
      key: 'actions',
      label: 'Actions',
      type: 'actions',
      width: 'w-32'
    });
    
    return columns;
  };

  const tableColumns = getTableColumns();

  // Render table cell based on column type
  const renderTableCell = (column, caseStudy) => {
    const getFieldValue = (caseStudy, possibleNames, defaultValue = null) => {
      for (const name of possibleNames) {
        if (caseStudy[name] !== undefined && caseStudy[name] !== null && caseStudy[name] !== '') {
          return caseStudy[name];
        }
      }
      return defaultValue;
    };

    switch (column.type) {
      
      case 'badge':
        if (column.key === 'status') {
          const status = getFieldValue(caseStudy, ['status']);
          return getStatusBadge(status);
        }
        return null;
      
      
      case 'actions':
        return (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => navigate(`/admin/case-study/${caseStudy.id}`)}
              className="text-blue-600 hover:text-blue-900"
              title="View Case Study Details"
              style={{ backgroundColor: '#d64a68', color: '#ffffff', padding: '4px 8px', borderRadius: '4px' }}
            >
              <Eye className="h-4 w-4" />
            </button>
            <button
              onClick={() => handleEdit(caseStudy)}
              className="text-primary-600 hover:text-primary-900"
              title="Edit Case Study"
              style={{ backgroundColor: '#d64a68', color: '#ffffff', padding: '4px 8px', borderRadius: '4px' }}
            >
              <Edit className="h-4 w-4" />
            </button>
            {/**<button className="text-gray-400 hover:text-gray-600">
              <MoreHorizontal className="h-4 w-4" />
            </button>*/}
          </div>
        );
      
      default:
        const textValue = getFieldValue(caseStudy, [column.key]);
        if ((column.key === 'description' || column.key === 'title') && textValue) {
          // Strip HTML tags for table display
          const plainText = textValue.replace(/<[^>]*>/g, '');
          return (
            <div className="text-sm text-gray-500 max-w-xs truncate" title={plainText}>
              {plainText || `No ${column.label}`}
            </div>
          );
        }
        return (
          <span className="text-sm text-gray-500">
            {textValue || `No ${column.label}`}
          </span>
        );
    }
  };

  // Fetch case studies on component mount
  useEffect(() => {
    fetchCaseStudies();
  }, []);

  const fetchCaseStudies = async () => {
    setLoading(true);
    try {
      const response = await getMsqCaseStudies();
      
      setApiResponse(response);
      
      if (response.success) {
        let caseStudyData = response.data;
        
        if (response.data && response.data.data) {
          caseStudyData = response.data.data;
        } else if (response.data && response.data.case_studies) {
          caseStudyData = response.data.case_studies;
        } else if (response.data && response.data.items) {
          caseStudyData = response.data.items;
        } else if (response.data && response.data.results) {
          caseStudyData = response.data.results;
        } else if (response.data && response.data.list) {
          caseStudyData = response.data.list;
        } else if (response.data && response.data.records) {
          caseStudyData = response.data.records;
        }
        
        if (Array.isArray(caseStudyData)) {
          console.log('Setting case studies from API:', caseStudyData);
          setCaseStudies(caseStudyData);
        } else {
          console.warn('API returned data but it\'s not an array:', caseStudyData);
          setCaseStudies([]);
        }
      } else {
        console.warn('API call failed:', response.error);
        setCaseStudies([]);
      }
    } catch (error) {
      console.warn('API error:', error);
      setCaseStudies([]);
    } finally {
      setLoading(false);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let response;
      if (editingCaseStudy) {
        response = await updateMsqCaseStudy(editingCaseStudy.id, formData);
      } else {
        response = await createMsqCaseStudy(formData);
      }

      if (response.success) {
        setShowModal(false);
        setEditingCaseStudy(null);
        resetForm();
        fetchCaseStudies();
      } else {
        // Fallback to mock data operations if API fails
        if (editingCaseStudy) {
          setCaseStudies(prev => prev.map(caseStudy => 
            caseStudy.id === editingCaseStudy.id 
              ? { ...caseStudy, ...formData, updated_at: new Date().toISOString() }
              : caseStudy
          ));
        } else {
          const newCaseStudy = {
            id: safeCaseStudies.length > 0 ? Math.max(...safeCaseStudies.map(a => a.id)) + 1 : 1,
            ...formData,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };
          setCaseStudies(prev => [...(Array.isArray(prev) ? prev : []), newCaseStudy]);
        }
        setShowModal(false);
        setEditingCaseStudy(null);
        resetForm();
      }
    } catch (error) {
      // Fallback to mock data operations if API throws error
      if (editingCaseStudy) {
        setCaseStudies(prev => prev.map(caseStudy => 
          caseStudy.id === editingCaseStudy.id 
            ? { ...caseStudy, ...formData, updated_at: new Date().toISOString() }
            : caseStudy
        ));
      } else {
        const newCaseStudy = {
          id: safeCaseStudies.length > 0 ? Math.max(...safeCaseStudies.map(a => a.id)) + 1 : 1,
          ...formData,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        setCaseStudies(prev => [...(Array.isArray(prev) ? prev : []), newCaseStudy]);
      }
      setShowModal(false);
      setEditingCaseStudy(null);
      resetForm();
    }
  };

  const handleEdit = (caseStudy) => {
    setEditingCaseStudy(caseStudy);
    let metaData = [];
    if (Array.isArray(caseStudy.meta)) {
      metaData = caseStudy.meta;
    } else if (caseStudy.meta && typeof caseStudy.meta === 'string') {
      metaData = [{
        pdf_name: '',
        pdf_description: caseStudy.meta,
        pdf_attach: ''
      }];
    }
    setFormData({
      title: caseStudy.title || '',
      status: caseStudy.status || 'active',
      description: caseStudy.description || '',
      module_number: caseStudy.module_number || '',
      video_url: caseStudy.video_url || '',
      meta: metaData
    });
    setUploadingMetaImages({});
    setShowModal(true);
  };

  const handleDelete = async (caseStudyId) => {
    if (window.confirm('Are you sure you want to delete this case study?')) {
      try {
        const response = await deleteMsqCaseStudy(caseStudyId);
        if (response.success) {
          fetchCaseStudies();
        } else {
          setCaseStudies(prev => prev.filter(caseStudy => caseStudy.id !== caseStudyId));
        }
      } catch (error) {
        setCaseStudies(prev => prev.filter(caseStudy => caseStudy.id !== caseStudyId));
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      status: 'active',
      description: '',
      module_number: '',
      video_url: '',
      meta: []
    });
    setUploadingMetaImages({});
  };

  const openCreateModal = () => {
    setEditingCaseStudy(null);
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCaseStudy(null);
    resetForm();
  };

  const filteredCaseStudies = searchTerm ? safeCaseStudies.filter(caseStudy => {
    const searchLower = searchTerm.toLowerCase();
    
    // Strip HTML tags from title and description for search
    const titleText = caseStudy.title ? caseStudy.title.replace(/<[^>]*>/g, '') : '';
    const descriptionText = caseStudy.description ? caseStudy.description.replace(/<[^>]*>/g, '') : '';
    
    return (
      (titleText && titleText.toLowerCase().includes(searchLower)) ||
      (descriptionText && descriptionText.toLowerCase().includes(searchLower)) ||
      (caseStudy.module_number && caseStudy.module_number.toLowerCase().includes(searchLower)) ||
      (caseStudy.video_url && caseStudy.video_url.toLowerCase().includes(searchLower))
    );
  }) : safeCaseStudies;

  const getStatusBadge = (status) => {
    const statusConfig = msqCaseStudyStatuses.find(s => s.value === status) || msqCaseStudyStatuses[0];
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusConfig.color}`}>
        {statusConfig.label}
      </span>
    );
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="">
      {/* Custom Quill Editor Styles */}
      <style dangerouslySetInnerHTML={{ __html: quillStyles }} />
      
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">MSQ Case Studies</h1>
          <p className="text-gray-600">Manage MSQ case studies and implementation examples.</p>
        </div>
        {/*<button 
          onClick={openCreateModal}
          className="btn-primary flex items-center space-x-2 mt-4 sm:mt-0"
          style={{ backgroundColor: '#d64a68', color: '#ffffff' }}
        >
          <Plus className="h-5 w-5" />
          <span>Add Case Study</span>
        </button>*/}
      </div>

      {/* Filters and Search 
      <div className="card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="relative flex-1 max-w-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search case studies..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>
          
          
        </div>
      </div>*/}

      {/* Case Studies Table */}
      <div className="card">
        {/* Table Header Info */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Case Studies List</h3>
              {/**<p className="text-sm text-gray-600">
                {apiResponse && apiResponse.success 
                  ? `Showing ${safeCaseStudies.length} case studies `//from API with ${tableColumns.length} columns` 
                  : `Showing ${safeCaseStudies.length} case studies with ${tableColumns.length} columns (${safeCaseStudies.length > 0 ? 'from fallback data' : 'no data available'})`
                }
              </p>*/}
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500">
                Last updated: {new Date().toLocaleTimeString()}
              </p>
            </div>
          </div>
        </div>
        
        <div className="overflow-x-auto table-container">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {tableColumns.map((column) => (
                  <th 
                    key={column.key}
                    className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${column.width}`}
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {safeCaseStudies.length === 0 ? (
                <tr>
                  <td colSpan={tableColumns.length} className="px-6 py-8 text-center text-gray-500">
                    <div className="flex flex-col items-center space-y-2">
                      <FileText className="h-12 w-12 text-gray-300" />
                      <p className="text-lg font-medium">No case studies found</p>
                      <p className="text-sm">
                        {apiResponse && apiResponse.success 
                          ? 'API returned no data or empty array' 
                          : 'No API response or using fallback data'
                        }
                      </p>
                      <button
                        onClick={fetchCaseStudies}
                        className="btn-secondary mt-2"
                        style={{ backgroundColor: '#d64a68', color: '#ffffff' }}
                      >
                        Try Again
                      </button>
                    </div>
                  </td>
                </tr>
              ) : filteredCaseStudies.length === 0 ? (
                <tr>
                  <td colSpan={tableColumns.length} className="px-6 py-8 text-center text-gray-500">
                    <div className="flex flex-col items-center space-y-2">
                      <Search className="h-12 w-12 text-gray-300" />
                      <p className="text-lg font-medium">No case studies match your search</p>
                      <p className="text-sm">Try adjusting your search terms</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredCaseStudies.map((caseStudy) => (
                  <tr key={caseStudy.id} className="hover:bg-gray-50">
                    {tableColumns.map((column) => (
                      <td key={column.key} className="px-6 py-4 whitespace-nowrap">
                        {renderTableCell(column, caseStudy)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Case Study Modal */}
      {showModal && (
        <div 
          className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
          onClick={closeModal}
        >
          <div 
            className="relative top-[10px] mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingCaseStudy ? 'Edit Case Study' : 'Create Case Study'}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600"
                  style={{ backgroundColor: '#d64a68', color: '#ffffff', padding: '4px 8px', borderRadius: '4px' }}
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Case Study Title *
                  </label>
                  <div className="border border-gray-300 rounded-md overflow-hidden">
                    <ReactQuill
                      value={formData.title || ''}
                      onChange={handleTitleChange}
                      modules={quillModules}
                      formats={quillFormats}
                      theme="snow"
                      style={{ height: 'auto', border: 'none' }}
                      placeholder="Enter case study title"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({...formData, status: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    >
                      {msqCaseStudyStatuses.map(status => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Module Number
                    </label>
                    <input
                      type="text"
                      value={formData.module_number}
                      onChange={(e) => setFormData({...formData, module_number: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <div className="border border-gray-300 rounded-md overflow-hidden">
                    <ReactQuill
                      value={formData.description || ''}
                      onChange={handleDescriptionChange}
                      modules={quillModules}
                      formats={quillFormats}
                      theme="snow"
                      style={{ height: 'auto', border: 'none' }}
                      placeholder="Enter case study description"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Video URL
                  </label>
                  <textarea
                    value={formData.video_url}
                    onChange={(e) => setFormData({...formData, video_url: e.target.value})}
                    rows={3}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="Enter video URL (e.g., https://www.youtube.com/watch?v=...)"
                  />
                </div>

                {/* Attachments Section */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Attachments
                    </label>
                    <button
                      type="button"
                      onClick={addMetaItem}
                      className="btn-secondary flex items-center space-x-2 text-sm"
                      style={{ backgroundColor: '#d64a68', color: '#ffffff' }}
                    >
                      <Plus className="h-4 w-4" />
                      <span>Add Attachment</span>
                    </button>
                  </div>

                  {(!formData.meta || !Array.isArray(formData.meta) || formData.meta.length === 0) ? (
                    <div className="text-center py-8 text-gray-500 border border-gray-200 rounded-lg bg-gray-50">
                      <p>No attachment added yet. Click "Add Attachment" to create one.</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {formData.meta.map((meta, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="text-md font-medium text-gray-700">Attachment #{index + 1}</h4>
                            <button
                              type="button"
                              onClick={() => removeMetaItem(index)}
                              className="text-red-600 hover:text-red-700 flex items-center space-x-1"
                              style={{ backgroundColor: '#d64a68', color: '#ffffff', padding: '4px 8px', borderRadius: '4px' }}
                            >
                              <Trash2 className="h-4 w-4" />
                              <span>Remove</span>
                            </button>
                          </div>

                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                PDF Name
                              </label>
                              <input
                                type="text"
                                value={meta.pdf_name || meta.meta_key || ''}
                                onChange={(e) => updateMetaItem(index, 'pdf_name', e.target.value)}
                                placeholder="Enter PDF name"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                PDF Description
                              </label>
                              <textarea
                                value={meta.pdf_description || meta.meta_value || ''}
                                onChange={(e) => updateMetaItem(index, 'pdf_description', e.target.value)}
                                placeholder="Enter PDF description"
                                rows="3"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center space-x-1">
                                <FileText className="h-4 w-4" />
                                <span>PDF Upload</span>
                              </label>

                              {meta.pdf_attach && (
                                <div className="mb-3">
                                  <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-md border border-gray-300">
                                    <FileText className="h-6 w-6 text-red-500" />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-gray-700">Current PDF</p>
                                      <a
                                        href={meta.pdf_attach}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs text-blue-600 hover:text-blue-800 underline"
                                      >
                                        View PDF
                                      </a>
                                    </div>
                                  </div>
                                </div>
                              )}

                              <div className="flex items-center space-x-4">
                                <div className="flex-shrink-0">
                                  {!meta.pdf_attach && (
                                    <div className="h-16 w-16 border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center bg-gray-50">
                                      <FileText className="h-6 w-6 text-gray-400" />
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1">
                                  <label className="cursor-pointer">
                                    <input
                                      type="file"
                                      accept=".pdf,application/pdf"
                                      onChange={(e) => handleMetaPdfUpload(index, e.target.files[0])}
                                      className="hidden"
                                      disabled={uploadingMetaImages[`meta_${index}`]}
                                    />
                                    <div
                                      className={`btn-secondary inline-flex items-center space-x-2 ${uploadingMetaImages[`meta_${index}`] ? 'opacity-50 cursor-not-allowed' : ''}`}
                                      style={{ backgroundColor: '#d64a68', color: '#ffffff' }}
                                    >
                                      {uploadingMetaImages[`meta_${index}`] ? (
                                        <>
                                          <LoadingSpinner />
                                          <span>Uploading...</span>
                                        </>
                                      ) : (
                                        <>
                                          <Upload className="h-4 w-4" />
                                          <span>{meta.pdf_attach ? 'Replace PDF' : 'Upload PDF'}</span>
                                        </>
                                      )}
                                    </div>
                                  </label>
                                  <p className="text-xs text-gray-500 mt-2">
                                    PDF format only, max 10MB
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <div className="flex justify-end space-x-3 pt-4 pb-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="btn-secondary"
                    style={{ backgroundColor: '#d64a68', color: '#ffffff' }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    style={{ backgroundColor: '#d64a68', color: '#ffffff' }}
                  >
                    {editingCaseStudy ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MSQCaseStudies;
